import formEl from './formEl';
import './components.less';
import googleMaps from './googleMaps';
import tableView from './tableView';
import txSelect from './txSelect';
import formHelpers from './formHelpers';
import mailgun from './mailgun';
import datePickers from './datePickers/datePickers';
import shopStyle from './shopStyle';
import ctrlC from './ctrlC';
import metadataEdit from './metadataEdit';
import jsonEditor from './jsonEditor';
import eventSelector from './eventSelector';
import reservationComponents from './reservationComponents';
import shopComponents from './shopComponents';
import dateRange from './dateRange';
import ticketDirectives from './ticketDirectives';
import CookieLaw from './CookieLaw';
import focusInput from './focusInput';
import companySelector from './companySelector';
import Avatar from './avatar/avatar';
import ModelPicker from './modelPicker/modelPicker';
import ClearInput from './clearInput';
import ScannerLoginModal from './scannerLoginModal/scannerLoginModal';
import VatInput from './vatInput/vatInput';

export default angular.module(
    'eventix.common.directives',
    [
        formEl,
        googleMaps,
        mailgun,
        datePickers,
        shopStyle,
        ctrlC,
        formHelpers,
        tableView,
        txSelect,
        jsonEditor,
        metadataEdit,
        reservationComponents,
        ticketDirectives,
        shopComponents,
        dateRange,
        eventSelector,
        CookieLaw,
        focusInput,
        companySelector,
        Avatar,
        ModelPicker,
        ClearInput,
        ScannerLoginModal,
        VatInput
    ])
    .directive('logReady', function($log, $timeout) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:logReady
         * @restrict element
         * @description
         * Debugging directive that will print a unix timestamp when the current element is rendered
         *
         * @param {String} logReady An identifying name to trace in the console
         */
        return {
            restrict: 'A',
            link: function( $scope, elem, attrs ) {
                elem.ready(function(){
                    $timeout(() => $log.log('logReady', attrs.logReady, Date.now()));
                });
            }
        };
    })
    .directive('responsiveRatio', function($window) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:responsiveRatio
         * @restrict element
         * @description
         * Maintain an aspect ratio of an element independent of the size of the screen
         *
         * @example
         * ```
         * <iframe style="width: 100%" responsive-ratio="{{ 210/297 }}" src="/docs/ticket.pdf"></iframe>
         * ```
         * @param {Number} responsiveRatio The ratio that should be maintained
         */
        return {
            restrict: 'A',
            link: function(scope, el, attrs) {
                attrs.$observe('responsiveRatio', r => scale(scope.ratio = r));
                scope.$watch(() => window.getComputedStyle(el[0]).width, scale);
                function scale() {
                    if(!scope.ratio) return;
                    var width = window.getComputedStyle(el[0]).width.slice(0,-2);
                    var height = width / scope.ratio;
                    el[0].style.height = height + 'px';
                }
            }
        };
    })
    .directive('exchangeCurrency', function(Currencies) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:exchangeCurrency
         * @restrict element
         * @requires eventix.common.services.Currencies
         * @description
         * Convert amount to another currency
         *
         * @param {Number} amount The amount of money
         * @param {String} convertTo The target currency.
         * @param {String} convertFrom The source currency
         * @param {String} locale The target locale
         */
        return {
            restrict: 'E',
            template: '<span>{{ converted | formatCurrency:convertTo:locale }}</span>',
            scope: { amount: '=', convertFrom: '=', convertTo: '=', locale: '=' },
            link: function(scope, el, attr) {
                scope.$on('CurrencyExchangeRatesUpdated', calculate);
                scope.$watch('convertFrom', calculate);
                scope.$watch('convertTo', calculate);
                scope.$watch('amount', calculate);
                scope.$watch('locale', calculate);
                function calculate() {
                    scope.converted = Currencies.exchange(scope.amount, scope.convertFrom, scope.convertTo);
                    scope.toLocale = scope.locale || event.locale;
                }
            }
        };
    })
    .component('versionLabel', {
        template: '<span ng-show="$ctrl.staging">{{ $ctrl.version }}</span>',
        controller: function($element) {
            this.staging = APP_ENV === 'staging';
            this.version = VERSION;
            if(this.staging) {
                $element.css({
                    display: 'inline-block',
                    position: 'fixed',
                    bottom: 0,
                    left: '50%',
                    background: 'rgba(255,255,255,0.8)',
                    opacity: '0.8',
                    zIndex: 999,
                    padding: '5px 10px',
                    transform: 'translateX(-50%)'
                });
            }
        }
    })
    .directive('uiSref', function() {
        return {
            restrict: 'A',
            priority: '10', //run after ui.router.uiSref
            link: function(scope, el, attrs) {
                if(attrs.uiSref && !attrs.href)
                    $(el).parent().addClass('no-url-found');
            }
        };
    })
    .directive('kcdRecompile', function($parse) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:kcdRecompile
         * @restrict element
         * @description
         * Recompile transcluded content if `kcdRecompile` changes. You can flag `useBoolean` in which case `kcdRecompile`
         * will be set to `false` afterwards.
         *
         * Gracefully borrowed from {@link http://kentcdodds.com/kcd-angular/#/kcd-recompile}
         *
         * @param {Any} kcdRecompile Variable that will be watched, triggering recompile
         * @param {Boolean} useBoolean Should watch be interpreted as a boolean or simply recompile on change
         */
        return {
            transclude: true,
            link: function link(scope, $el, attrs, ctrls, transclude) {
                var previousElements;

                compile();

                function compile() {
                    transclude(scope, function(clone, clonedScope) {
                        // transclude creates a clone containing all children elements;
                        // as we assign the current scope as first parameter, the clonedScope is the same
                        previousElements = clone;
                        $el.append(clone);
                    });
                }

                function recompile() {
                    if (previousElements) {
                        previousElements.remove();
                        previousElements = null;
                        $el.empty();
                    }

                    compile();
                }

                scope.$watch(attrs.kcdRecompile, function(_new, _old) {
                    var useBoolean = attrs.hasOwnProperty('useBoolean');
                    if ((useBoolean && (!_new || _new === 'false')) || (!useBoolean && (!_new || _new === _old)))
                        return;
                    // reset kcdRecompile to false if we're using a boolean
                    if (useBoolean)
                        $parse(attrs.kcdRecompile).assign(scope, false);

                    recompile();
                }, typeof $parse(attrs.kcdRecompile)(scope) === 'object');
            }
        };
    })
    .directive('windowResize', function($window) {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:windowResize
         * @restrict attribute
         * @description
         * Callback for window resize event
         *
         * @example
         * <pre>
         * <div window-resize="vm.resize(width, height)"></div>
         * </pre>
         *
         * @param {Function} windowResize Callback receiving width & height
         */
        return {
            restrict: 'A',
            scope: { callback: '&windowResize' },
            link: function(scope, element, attrs) {
                $($window).on('resize',_.debounce(() => {
                    var w = $($window).width(),
                        h = $($window).height();
                    scope.callback({width: w,height: h});
                },300));
            }
        };
    })
    .directive('code', function() {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:code
         * @restrict element
         * @description
         * Escape HTML contents to display code fragments without having to type annoying `&lt;div&gt;`
         *
         * @example
         * <pre>
         *     <h1>I will be displayed as code, not rendered as Heading</h1>
         * </pre>
         */
        return {
            restrict: 'E',
            priority: 10000,
            compile: function(tElm) {
                var staticHTML = tElm[0].innerHTML.replace(/^(\r\n|\r|\n)/m, '');
                return function(scope, el, attr, ctrl, transcludeFn) {
                    el.text(staticHTML);
                };
            }
        };
    }).name;
